<template>
  <div class="flex-1 flex flex-col h-full overflow-hidden">
    <!-- 图表显示区域 -->
    <div class="flex-1 flex justify-center items-center overflow-hidden p-8" ref="editorContent">
      <div class="chart-container" :style="chartContainerStyle">
        <div class="content-wrapper" :style="contentWrapperStyle">
          <div 
            ref="chart" 
            class="elegant-card" 
            :style="{ 
              backgroundColor, 
              width: `${imageWidth}px`, 
              height: `${imageHeight}px`,
              minWidth: '300px',
              minHeight: '200px'
            }"
          />
        </div>
      </div>
    </div>
    
    <!-- 缩放控制栏 -->
    <div class="toolbar-elegant px-6 py-4 border-t border-slate-200/60 flex justify-center items-center">
      <div class="control-panel-elegant flex items-center space-x-4 px-6 py-3">
        <el-button 
          circle 
          @click="$emit('zoomOut')" 
          class="w-10 h-10 p-0 btn-secondary-elegant rounded-full flex items-center justify-center"
          size="small"
        >
          <el-icon class="text-sm"><Remove /></el-icon>
        </el-button>
        
        <div class="flex items-center space-x-2 px-4 py-2 bg-slate-100 rounded-xl">
          <span class="text-sm font-semibold text-slate-600 min-w-[60px] text-center">
          {{ Math.round(zoomLevel * 100) }}%
          </span>
        </div>
        
        <el-button 
          circle 
          @click="$emit('zoomIn')" 
          class="w-10 h-10 p-0 btn-secondary-elegant rounded-full flex items-center justify-center"
          size="small"
        >
          <el-icon class="text-sm"><Plus /></el-icon>
        </el-button>
        
        <div class="w-px h-6 bg-slate-300" />
        
        <el-button 
          circle 
          @click="$emit('resetZoom')" 
          class="w-10 h-10 p-0 btn-secondary-elegant rounded-full flex items-center justify-center"
          size="small"
        >
          <el-icon class="text-sm"><Refresh /></el-icon>
        </el-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { Remove, Plus, Refresh } from '@element-plus/icons-vue'
import * as echarts from 'echarts'
import { ElMessage } from 'element-plus'

const props = defineProps({
  imageWidth: {
    type: Number,
    default: 512
  },
  imageHeight: {
    type: Number,
    default: 512
  },
  backgroundColor: {
    type: String,
    default: '#FFFFFF'
  },
  zoomLevel: {
    type: Number,
    default: 1
  },
  chartData: {
    type: Array,
    default: () => []
  },
  orient: {
    type: String,
    default: 'horizontal'
  },
  nodeAlign: {
    type: String,
    default: 'right'
  },
  labelPosition: {
    type: String,
    default: 'top'
  },
  lineStyleColor: {
    type: String,
    default: 'source'
  },
  selectedLineColorCurveness: {
    type: Number,
    default: 0.1
  }
})

defineEmits(['zoomIn', 'zoomOut', 'resetZoom'])

// 导出图片方法
const exportLayout = () => {
  if (!chartInstance) {
    ElMessage.error('图表未初始化')
    return
  }

  try {
    // 获取图表的 base64 图片数据
    const base64 = chartInstance.getDataURL({
      type: 'png',
      backgroundColor: props.backgroundColor,
      pixelRatio: 2, // 提高导出图片质量
    })

    // 创建下载链接
    const link = document.createElement('a')
    link.download = `sankey-chart-${new Date().getTime()}.png`
    link.href = base64
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    ElMessage.success('导出成功')
  } catch (error) {
    console.error('导出失败:', error)
    ElMessage.error('导出失败，请稍后重试')
  }
}

const chartContainerStyle = computed(() => ({
  width: '100%',
  height: '100%',
  overflow: 'auto',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}))

const contentWrapperStyle = computed(() => ({
  transform: `scale(${props.zoomLevel})`,
  transformOrigin: 'center center',
  transition: 'transform 0.2s ease-in-out'
}))

const chart = ref(null)
let chartInstance = null

const initChart = () => {
  if (chart.value && !chartInstance) {
    chartInstance = echarts.init(chart.value)
    updateChart()
    
    // 响应式处理
    const resizeObserver = new ResizeObserver(() => {
      chartInstance?.resize()
    })
    resizeObserver.observe(chart.value)
    
    // 窗口大小改变时重新调整
    window.addEventListener('resize', () => {
      chartInstance?.resize()
    })
  }
}

const updateChart = () => {
  if (!chartInstance) return
  
  // 生成随机颜色
  const getRandomColor = () => {
    const colors = [
      '#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de',
      '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc', '#5D9CEC',
      '#4FC3F7', '#81C784', '#FFB74D', '#F06292', '#A1887F',
      '#90A4AE', '#FF8A65', '#FFAB40', '#42A5F5', '#AB47BC'
    ]
    return colors[Math.floor(Math.random() * colors.length)]
  }
  
  // 处理数据
  const nodes = []
  const links = []
  const nodesSet = new Set()
  
  props.chartData.forEach(item => {
    if (!nodesSet.has(item.source)) {
      nodes.push({ 
        name: item.source, 
        itemStyle: { color: getRandomColor() } 
      })
      nodesSet.add(item.source)
    }
    if (!nodesSet.has(item.target)) {
      nodes.push({ 
        name: item.target, 
        itemStyle: { color: getRandomColor() } 
      })
      nodesSet.add(item.target)
    }
    links.push({ 
      source: item.source, 
      target: item.target, 
      value: item.value 
    })
  })
  
  // 如果没有数据，显示示例数据
  if (nodes.length === 0) {
    const sampleData = [
      { name: 'A', itemStyle: { color: '#5470c6' } },
      { name: 'B', itemStyle: { color: '#91cc75' } },
      { name: 'C', itemStyle: { color: '#fac858' } },
      { name: 'D', itemStyle: { color: '#ee6666' } },
    ]
    const sampleLinks = [
      { source: 'A', target: 'B', value: 20 },
      { source: 'A', target: 'C', value: 30 },
      { source: 'B', target: 'D', value: 15 },
      { source: 'C', target: 'D', value: 25 },
    ]
    nodes.push(...sampleData)
    links.push(...sampleLinks)
  }
  
  const option = {
    backgroundColor: props.backgroundColor,
    title: {
      text: nodes.length > 4 ? '' : '桑基图示例',
      left: 'center',
      top: 20,
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#64748b'
      }
    },
    tooltip: {
      trigger: 'item',
      triggerOn: 'mousemove',
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      textStyle: {
        color: '#fff'
      },
      borderRadius: 8,
      padding: [8, 12]
    },
    series: [
      {
        type: 'sankey',
        data: nodes,
        links: links,
        right: 20,
        bottom: 20,
        left: 20,
        top: nodes.length > 4 ? 20 : 60,
        width: 'auto',
        height: 'auto',
        orient: props.orient,
        nodeAlign: props.nodeAlign,
        draggable: true,
        focusNodeAdjacency: 'allEdges',
        itemStyle: {
          borderWidth: 1,
          borderColor: '#aaa'
        },
        label: {
          position: props.labelPosition,
          fontSize: 12,
          fontWeight: 'bold',
          color: '#333'
        },
        lineStyle: {
          color: props.lineStyleColor,
          curveness: props.selectedLineColorCurveness,
          opacity: 0.7
        },
        emphasis: {
          focus: 'adjacency',
          lineStyle: {
            opacity: 0.9
          }
        },
        animationDuration: 1000,
        animationEasing: 'cubicOut'
      }
    ]
  }
  
  chartInstance.setOption(option, true)
}

// 监听数据变化
watch([
  () => props.chartData,
  () => props.backgroundColor,
  () => props.orient,
  () => props.nodeAlign,
  () => props.labelPosition,
  () => props.lineStyleColor,
  () => props.selectedLineColorCurveness
], () => {
  nextTick(() => {
    updateChart()
  })
}, { deep: true })

onMounted(() => {
  nextTick(() => {
    initChart()
  })
})

onUnmounted(() => {
  if (chartInstance) {
    chartInstance.dispose()
    chartInstance = null
  }
})

defineExpose({
  chart,
  chartInstance,
  updateChart,
  exportLayout
})
</script>

<style scoped>
.chart-container {
  max-width: 100%;
  max-height: 100%;
  overflow: auto;
}

.content-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
}

/* 响应式图表容器 */
@media (max-width: 768px) {
  .chart-container {
    max-width: 100%;
    overflow: auto;
  }
  .content-wrapper {
    max-width: 100%;
    overflow: hidden;
  }
}
</style>